type Rgb = {
  readonly r: number;
  readonly g: number;
  readonly b: number;
};

// eslint-disable-next-line no-restricted-syntax -- runtime contrast output must be an absolute colour, not a theme-relative token.
const BLACK = "#000000";
// eslint-disable-next-line no-restricted-syntax -- runtime contrast output must be an absolute colour, not a theme-relative token.
const WHITE = "#ffffff";
const CSS_NUMBER = /^[+-]?(?:\d+(?:\.\d*)?|\.\d+)(?:e[+-]?\d+)?$/i;

function parseNumber(value: string): number | undefined {
  if (!CSS_NUMBER.test(value)) return undefined;
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : undefined;
}

function clampByte(value: number): number | undefined {
  return Number.isFinite(value) && value >= 0 && value <= 255
    ? value
    : undefined;
}

function parseAlpha(value: string | undefined): number | undefined {
  if (value == null) return 1;
  const alpha = value.endsWith("%")
    ? parseNumber(value.slice(0, -1))
    : parseNumber(value);
  if (alpha == null) return undefined;
  const normalized = value.endsWith("%") ? alpha / 100 : alpha;
  return normalized >= 0 && normalized <= 1 ? normalized : undefined;
}

function hslToRgb(hue: number, saturation: number, lightness: number): Rgb {
  const h = ((hue % 360) + 360) % 360;
  const k = (n: number): number => (n + h / 30) % 12;
  const a = saturation * Math.min(lightness, 1 - lightness);
  const channel = (n: number): number =>
    lightness - a * Math.max(-1, Math.min(k(n) - 3, Math.min(9 - k(n), 1)));
  return {
    r: channel(0) * 255,
    g: channel(8) * 255,
    b: channel(4) * 255,
  };
}

function parseHex(value: string): Rgb | undefined {
  const match = /^#([0-9a-f]{3}|[0-9a-f]{6})$/i.exec(value);
  if (!match) return undefined;
  const body =
    match[1]!.length === 3
      ? [...match[1]!].map((digit) => digit + digit).join("")
      : match[1]!;
  const packed = Number.parseInt(body, 16);
  return {
    r: (packed >> 16) & 255,
    g: (packed >> 8) & 255,
    b: packed & 255,
  };
}

function parseRgb(value: string): Rgb | undefined {
  const body = /^rgba?\((.*)\)$/i.exec(value)?.[1]?.trim();
  if (!body) return undefined;
  const normalized = body.replace(/\s*\/\s*/, ",");
  const parts = normalized.includes(",")
    ? normalized.split(",").map((part) => part.trim())
    : normalized.split(/\s+/);
  if (parts.length < 3 || parts.length > 4) return undefined;
  const channels = parts.slice(0, 3).map((part) => {
    const value = part.endsWith("%")
      ? parseNumber(part.slice(0, -1))
      : parseNumber(part);
    if (value == null) return Number.NaN;
    return part.endsWith("%") ? (value / 100) * 255 : value;
  });
  const alpha = parseAlpha(parts[3]);
  const [r, g, b] = channels.map(clampByte);
  if (r == null || g == null || b == null || alpha !== 1) return undefined;
  return { r, g, b };
}

function parseHsl(value: string): Rgb | undefined {
  const body = /^hsla?\((.*)\)$/i.exec(value)?.[1]?.trim();
  if (!body) return undefined;
  const normalized = body.replace(/\s*\/\s*/, ",");
  const parts = normalized.includes(",")
    ? normalized.split(",").map((part) => part.trim())
    : normalized.split(/\s+/);
  if (parts.length < 3 || parts.length > 4) return undefined;
  if (!parts[1]?.endsWith("%") || !parts[2]?.endsWith("%")) return undefined;
  const hue = parseNumber(parts[0]!);
  const saturationValue = parseNumber(parts[1].slice(0, -1));
  const lightnessValue = parseNumber(parts[2].slice(0, -1));
  const alpha = parseAlpha(parts[3]);
  if (
    hue == null ||
    saturationValue == null ||
    lightnessValue == null ||
    saturationValue < 0 ||
    saturationValue > 100 ||
    lightnessValue < 0 ||
    lightnessValue > 100 ||
    alpha !== 1
  )
    return undefined;
  const saturation = saturationValue / 100;
  const lightness = lightnessValue / 100;
  return hslToRgb(hue, saturation, lightness);
}

function parseOpaqueColor(value: string): Rgb | undefined {
  const normalized = value.trim().toLowerCase();
  return (
    parseHex(normalized) ??
    parseRgb(normalized) ??
    parseHsl(normalized) ??
    undefined
  );
}

function relativeLuminance({ r, g, b }: Rgb): number {
  const linear = [r, g, b].map((channel) => {
    const srgb = channel / 255;
    return srgb <= 0.03928 ? srgb / 12.92 : ((srgb + 0.055) / 1.055) ** 2.4;
  });
  return 0.2126 * linear[0]! + 0.7152 * linear[1]! + 0.0722 * linear[2]!;
}

/**
 * Picks the higher-contrast black or white foreground for an opaque runtime
 * colour. Runtime awareness and organisation colours cannot use a fixed theme
 * token: their luminance is user data. Unknown, transparent, named, or
 * variable-based colours return `undefined` so the caller's semantic class
 * remains the explicit fallback.
 */
export function getContrastingTextColor(
  backgroundColor: string,
): typeof BLACK | typeof WHITE | undefined {
  const background = parseOpaqueColor(backgroundColor);
  if (!background) return undefined;
  const luminance = relativeLuminance(background);
  const blackContrast = (luminance + 0.05) / 0.05;
  const whiteContrast = 1.05 / (luminance + 0.05);
  return blackContrast >= whiteContrast ? BLACK : WHITE;
}
